import { useState, useEffect } from 'react';
import api from '../../api';
import Combobox from '../../components/Combobox';
import DataTable from '../../components/DataTable';

const money = (n) => `${parseFloat(n).toLocaleString('ru')} ₽`;
const OPERATION_LABELS = { приход: 'Приход', поступление: 'Поступление', расход: 'Расход', выбытие: 'Выбытие' };
// Какие «Операции» вообще возможны для прихода/расхода денег по банку — реальное движение денег
// (пришли/ушли) не меняется, а вот бизнес-смысл (обычная выручка/расход или просто перемещение
// денег, заём, дивиденды и т.д.) как раз и определяет, что выбрать здесь.
const OPERATIONS_BY_DIRECTION = { income: ['приход', 'поступление'], expense: ['расход', 'выбытие'] };

export default function Queue() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [busyMode, setBusyMode] = useState(null); // 'single' | 'similar'
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false); // false | 'plain' | 'similar'

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/finance/cashflow/queue/list');
      setItems(data);
      const ids = new Set(data.map(tx => tx.id));
      setSelected(s => s.filter(id => ids.has(id)));
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get('/finance/meta').then(r => setMeta(r.data)); load(); }, []);

  const setChoiceField = (id, field, value) => setChoice(c => ({ ...c, [id]: { ...c[id], [field]: value } }));

  // Если статью выбрали раньше «Операции» — операция уже однозначно известна по самой статье
  // (у каждой статьи в справочнике зафиксирован её вид операции), простановка вручную лишняя.
  const pickArticle = (id, articleId) => {
    setChoiceField(id, 'article_id', articleId);
    const picked = meta.articles.find(a => String(a.id) === String(articleId));
    if (picked?.operation_type) setChoiceField(id, 'operation_type', picked.operation_type);
    if (!articleId) setSelected(s => s.filter(x => x !== id));
  };

  const categorize = async (id, alsoSimilar) => {
    const c = choice[id];
    if (!c?.article_id) return;
    setBusyId(id);
    setBusyMode(alsoSimilar ? 'similar' : 'single');
    try {
      const { data } = await api.patch(`/finance/cashflow/queue/${id}`, {
        article_id: Number(c.article_id), project_id: c.project_id || null, also_similar: !!alsoSimilar,
      });
      setNote(alsoSimilar
        ? (data.backfilled > 0 ? `Готово. Заодно автоматически размечено ещё ${data.backfilled} похожих операций.` : 'Готово. Похожих операций не нашлось.')
        : 'Готово.');
      load();
    } finally { setBusyId(null); setBusyMode(null); }
  };

  const ignore = async (id) => {
    if (!window.confirm('Пометить операцию как не требующую записи в ДДС (например, внутренний технический платёж)?')) return;
    setBusyId(id);
    try { await api.patch(`/finance/cashflow/queue/${id}/ignore`); load(); } finally { setBusyId(null); }
  };

  // Проставляем каждую отмеченную операцию явным выбором пользователя (без also_similar на
  // каждый запрос — иначе N отмеченных строк означали бы N полных проходов по всей очереди).
  // Для «...и похожие» доразметку остальной очереди запускаем один раз в конце, отдельным вызовом.
  const bulkApply = async (alsoSimilar) => {
    const ids = selected.filter(id => choice[id]?.article_id);
    if (!ids.length) return;
    setBulkSaving(alsoSimilar ? 'similar' : 'plain');
    try {
      const results = await Promise.allSettled(ids.map(id => {
        const c = choice[id];
        return api.patch(`/finance/cashflow/queue/${id}`, {
          article_id: Number(c.article_id), project_id: c.project_id || null, also_similar: false,
        });
      }));
      const okCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;
      const skipped = selected.length - ids.length;

      let backfilled = 0;
      if (alsoSimilar && okCount > 0) {
        const { data } = await api.post('/finance/cashflow/queue/backfill');
        backfilled = data.backfilled;
      }

      setNote(
        `Сохранено: ${okCount}.`
        + (failCount ? ` Не удалось: ${failCount}.` : '')
        + (skipped ? ` Пропущено без статьи: ${skipped}.` : '')
        + (alsoSimilar ? (backfilled > 0 ? ` Заодно автоматически размечено ещё ${backfilled} похожих операций.` : ' Похожих операций не нашлось.') : '')
      );
      setSelected([]);
      load();
    } finally { setBulkSaving(false); }
  };

  if (loading || !meta) return <div className="loading-text">Загрузка...</div>;

  const columns = [
    { key: 'operation_date', label: 'Дата', render: tx => new Date(tx.operation_date).toLocaleDateString('ru'), sortValue: tx => tx.operation_date },
    {
      key: 'direction', label: 'Тип',
      render: tx => <span className={`badge ${tx.direction === 'income' ? 'badge-paid' : 'badge-new'}`}>{tx.direction === 'income' ? 'Приход' : 'Расход'}</span>,
      filterValue: tx => (tx.direction === 'income' ? 'Приход' : 'Расход'),
    },
    { key: 'amount', label: 'Сумма', render: tx => money(tx.amount), sortValue: tx => Number(tx.amount), filterable: false },
    {
      key: 'contractor_name', label: 'Контрагент',
      render: tx => <>{tx.contractor_name || '—'}{tx.contractor_inn ? ` (ИНН ${tx.contractor_inn})` : ''}</>,
      filterValue: tx => `${tx.contractor_name || '—'}${tx.contractor_inn ? ` (ИНН ${tx.contractor_inn})` : ''}`,
    },
    { key: 'purpose', label: 'Назначение платежа', render: tx => <span style={{ maxWidth: 260, display: 'inline-block' }}>{tx.purpose || '—'}</span>, filterValue: tx => tx.purpose || '—' },
    {
      key: 'operation_type', label: 'Операция', sortable: false, filterable: false,
      render: tx => {
        const c = choice[tx.id] || {};
        const availableOps = OPERATIONS_BY_DIRECTION[tx.direction];
        return (
          <select
            value={c.operation_type || ''}
            onChange={e => {
              setChoiceField(tx.id, 'operation_type', e.target.value || undefined);
              setChoiceField(tx.id, 'article_id', '');
              setSelected(s => s.filter(id => id !== tx.id));
            }}
            style={{ minWidth: 150 }}
          >
            <option value="">Любая</option>
            {availableOps.map(op => <option key={op} value={op}>{OPERATION_LABELS[op]}</option>)}
          </select>
        );
      },
    },
    {
      key: 'article_id', label: 'Статья', sortable: false, filterable: false,
      render: tx => {
        const c = choice[tx.id] || {};
        const articlesForOp = meta.articles.filter(a => a.op_type === tx.direction && (!c.operation_type || a.operation_type === c.operation_type));
        return (
          <div style={{ minWidth: 220 }}>
            <Combobox
              value={c.article_id || ''}
              onChange={v => pickArticle(tx.id, v)}
              options={articlesForOp.map(a => ({ value: a.id, label: `${a.section_name} / ${a.name} (${OPERATION_LABELS[a.operation_type] || '—'})` }))}
              placeholder="Выберите статью"
            />
          </div>
        );
      },
    },
    {
      key: 'project_id', label: 'Проект', sortable: false, filterable: false,
      render: tx => {
        const c = choice[tx.id] || {};
        return (
          <div style={{ minWidth: 200 }}>
            <Combobox
              value={c.project_id || ''}
              onChange={v => setChoiceField(tx.id, 'project_id', v)}
              options={meta.projects.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Без привязки"
            />
          </div>
        );
      },
    },
    {
      key: 'actions', label: '', sortable: false, filterable: false,
      render: tx => {
        const c = choice[tx.id] || {};
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <button className="btn-primary btn-sm" disabled={busyId === tx.id || !c.article_id} onClick={() => categorize(tx.id, false)}>
              {busyId === tx.id && busyMode === 'single' ? 'Сохраняю...' : 'Сохранить'}
            </button>{' '}
            <button className="btn-secondary btn-sm" disabled={busyId === tx.id || !c.article_id} onClick={() => categorize(tx.id, true)} title="Сохранить эту операцию и сразу разметить все похожие в очереди">
              {busyId === tx.id && busyMode === 'similar' ? 'Ищу похожие (до минуты)...' : 'Сохранить и похожие'}
            </button>{' '}
            <button className="btn-secondary btn-sm" disabled={busyId === tx.id} onClick={() => ignore(tx.id)}>Пропустить</button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <p className="text-muted mb-2">
        Операции банка, для которых не нашлось ни точного правила, ни достаточно похожей ранее размеченной операции.
        Столбцы можно отфильтровать (значок фильтра в шапке). Выставьте операцию/статью/проект в нужных строках,
        отметьте их галочкой слева и нажмите «Сохранить выбранные» или «Сохранить выбранные и похожие» — применится
        сразу ко всем отмеченным (второе — ещё и разметит все похожие операции в остальной очереди, может занять до минуты).
        «Сохранить» и «Сохранить и похожие» в конце строки по-прежнему работают для одной операции.
        Новые операции в будущем разметятся автоматически по уже заведённым правилам.
      </p>
      <div className="toolbar mb-2">
        <div className="stat-card" style={{ maxWidth: 280 }}>
          <div className="text-muted" style={{ fontSize: 12 }}>Требуют разметки</div>
          <div style={{ fontSize: 24, fontWeight: 600 }}>{items.length}</div>
        </div>
        {selected.length > 0 && (
          <>
            <button className="btn-primary btn-sm" disabled={!!bulkSaving} onClick={() => bulkApply(false)}>
              {bulkSaving === 'plain' ? 'Сохраняю...' : `Сохранить выбранные (${selected.length})`}
            </button>
            <button className="btn-secondary btn-sm" disabled={!!bulkSaving} onClick={() => bulkApply(true)} title="Сохранить отмеченные операции и разметить все похожие в остальной очереди">
              {bulkSaving === 'similar' ? 'Ищу похожие (до минуты)...' : `Сохранить выбранные и похожие (${selected.length})`}
            </button>
          </>
        )}
      </div>
      {note && <div className="stat-card mb-2" style={{ maxWidth: 480 }}>{note}</div>}
      {items.length === 0 ? (
        <div className="empty-state">Очередь пуста — все операции размечены</div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          initialSort={{ key: 'operation_date', dir: 'desc' }}
          emptyText="Ничего не найдено"
          selectedIds={selected}
          onSelectedChange={setSelected}
          isRowSelectable={tx => !!choice[tx.id]?.article_id}
          canExport
          exportFilename="Категоризация"
        />
      )}
    </div>
  );
}
